'use client';

import { cn } from '@korvo/core/lib/utils';
import {
  Brain,
  ChevronDown,
  ChevronRight,
  Cpu,
  FileText,
  Home,
  KeyRound,
  MailPlus,
  Settings,
  Sliders,
  Users,
} from 'lucide-react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { type ReactNode,useEffect, useState } from 'react';

/**
 * @fileoverview Client shell for rendering the admin settings layout UI.
 *
 * @purpose Render the two-column admin settings navigation and content shell while handling
 *          interactions such as the expandable modules tree.
 * @usage Imported by the admin settings layout server component (`src/app/admin/settings/layout.tsx`)
 *        after the server has authorized the current request.
 * @ownership Admin UI (shell package)
 * @dependencies
 * - lucide-react (icons used by the navigation)
 * - next/navigation (client path helper)
 * - @korvo/core/lib/utils (cn helper for classnames)
 * @restrictions
 * - DO NOT import server-only hooks or data directly inside this component.
 * - DO NOT render this component unless the server layout already verified ADMIN access.
 * @docs
 * - docs/SETTINGS_GUIDELINES.md (admin settings architecture)
 */

const navigationSections = [
  {
    id: 'core',
    label: 'Core',
    items: [
      {
        id: 'system',
        label: 'System',
        icon: Settings,
        description: 'Core system configuration',
        href: '/admin/settings/system',
      },
      {
        id: 'users',
        label: 'Users',
        icon: Users,
        description: 'User management and permissions',
        href: '/admin/settings/users',
      },
      {
        id: 'registration-codes',
        label: 'Registration Codes',
        icon: KeyRound,
        description: 'Registration codes and print customization',
        href: '/admin/settings/registration-codes',
      },
      {
        id: 'invites',
        label: 'Invites',
        icon: MailPlus,
        description: 'Email invitations and onboarding',
        href: '/admin/settings/invites',
      },
      {
        id: 'homepage',
        label: 'Homepage',
        icon: Home,
        description: 'Homepage and custom pages configuration',
        href: '/admin/settings/homepage',
      },
    ],
  },
  {
    id: 'ai',
    label: 'AI & Modules',
    items: [
      {
        id: 'portal',
        label: 'Portal',
        icon: Cpu,
        description: 'LLM provider and model settings',
        href: '/admin/settings/portal',
      },
      {
        id: 'configs',
        label: 'Prompt Configs',
        icon: Sliders,
        description: 'Prompt/session config namespaces',
        href: '/admin/settings/configs',
      },
      {
        id: 'modules',
        label: 'Modules',
        icon: Brain,
        description: 'Module-specific configurations',
        href: '/admin/settings/modules',
      },
    ],
  },
  {
    id: 'governance',
    label: 'Governance',
    items: [
      {
        id: 'audit',
        label: 'Audit Log',
        icon: FileText,
        description: 'Review system and chat audits',
        href: '/admin/settings/audit',
      },
      {
        id: 'transcripts',
        label: 'Transcripts',
        icon: FileText,
        description: 'Recent conversation transcripts',
        href: '/admin/settings/transcripts',
      },
    ],
  },
];

type AdminPage = {
  title: string;
  description: string;
  href: string;
  icon: string;
};

type ModuleTreeItem = {
  id: string;
  name: string;
  adminPages: AdminPage[];
};

export function AdminSettingsClientLayout({
  children,
}: {
  children: ReactNode;
}) {
  const pathnameRaw = usePathname();
  const pathname = pathnameRaw !== null && pathnameRaw !== undefined ? pathnameRaw : '';
  const [modulesExpanded, setModulesExpanded] = useState(false);
  const [moduleTree, setModuleTree] = useState<ModuleTreeItem[]>([]);
  const [loadingModules, setLoadingModules] = useState(false);
  useEffect(() => {
    if (pathname.startsWith('/admin/settings/modules') || pathname.match(/\/\w+\/admin\//)) {
      setTimeout(() => setModulesExpanded(true), 0);
    }
  }, [pathname]);

  useEffect(() => {
    if (modulesExpanded && moduleTree.length === 0 && !loadingModules) {
      setTimeout(() => setLoadingModules(true), 0);
      fetch('/api/admin/modules/settings-tree')
        .then((res) => res.json())
        .then((data: { modules?: ModuleTreeItem[] }) => {
          setModuleTree(data.modules || []);
        })
        .catch((err) => {
          console.error('Failed to load module tree:', err);
        })
        .finally(() => {
          setLoadingModules(false);
        });
    }
  }, [modulesExpanded, moduleTree.length, loadingModules]);

  const navContent = (
    <nav>
          <div className="space-y-6">
            {navigationSections.map((section) => (
              <div key={section.id} className="space-y-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-foreground">
                  {section.label}
                </p>
                <ul className="space-y-2">
                  {section.items.map((item) => {
                    const isActive =
                      pathname === item.href ||
                      (item.id === 'modules' && pathname.startsWith('/admin/settings/modules')) ||
                      (item.id === 'registration-codes' && pathname.startsWith('/admin/settings/registration-codes')) ||
                      (item.id === 'transcripts' && pathname.startsWith('/admin/settings/transcripts'));
                    const Icon = item.icon;
                    const isModules = item.id === 'modules';

                    return (
                      <li key={item.id}>
                        {isModules ? (
                          <div>
                            <button
                              onClick={() => setModulesExpanded(!modulesExpanded)}
                              className={cn(
                                'w-full text-left py-3 px-4 rounded-lg transition-colors flex items-center justify-between',
                                isActive
                                  ? 'korvo-bg-active korvo-text-active korvo-border-active border'
                                  : 'text-foreground-default'
                              )}
                              onMouseEnter={(e) => {
                                if (!isActive) {
                                  e.currentTarget.style.backgroundColor = 'var(--background-muted)';
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!isActive) {
                                  e.currentTarget.style.backgroundColor = 'transparent';
                                }
                              }}
                            >
                              <div className="flex items-center gap-3 flex-1">
                                <Icon className="h-4 w-4" />
                                <div className="flex-1">
                                  <div className="font-medium">{item.label}</div>
                                  <div className="text-xs text-foreground-muted mt-1">
                                    {item.description}
                                  </div>
                                </div>
                              </div>
                              {modulesExpanded ? (
                                <ChevronDown className="h-4 w-4" />
                              ) : (
                                <ChevronRight className="h-4 w-4" />
                              )}
                            </button>
                            {modulesExpanded && (
                              <div className="ml-4 mt-2 space-y-1 border-l-2 pl-4" style={{ borderColor: 'var(--border-default)' }}>
                                <Link
                                  href="/admin/settings/modules"
                                  className={cn(
                                    'block py-2 px-3 rounded text-sm transition-colors',
                                    pathname === '/admin/settings/modules'
                                      ? 'korvo-bg-active korvo-text-active font-medium'
                                      : 'text-foreground-muted'
                                  )}
                                  onMouseEnter={(e) => {
                                    if (pathname !== '/admin/settings/modules') {
                                      e.currentTarget.style.backgroundColor = 'var(--background-muted)';
                                    }
                                  }}
                                  onMouseLeave={(e) => {
                                    if (pathname !== '/admin/settings/modules') {
                                      e.currentTarget.style.backgroundColor = 'transparent';
                                    }
                                  }}
                                >
                                  Overview
                                </Link>
                                {loadingModules ? (
                                  <div className="py-2 px-3 text-sm text-foreground-muted">
                                    Loading modules...
                                  </div>
                                ) : (
                                  moduleTree
                                    .filter((module) => module.adminPages.length > 0)
                                    .map((module) => (
                                      <div key={module.id}>
                                        <div className="py-1">
                                          <div className="text-xs font-semibold text-foreground-muted uppercase tracking-wide px-3 py-1">
                                            {module.name}
                                          </div>
                                          <div className="space-y-0.5">
                                            {module.adminPages.map((page) => {
                                              const pageActive = pathname === page.href;
                                              return (
                                                <Link
                                                  key={page.href}
                                                  href={page.href}
                                                  className={cn(
                                                    'block py-1.5 px-3 rounded text-sm transition-colors',
                                                    pageActive
                                                      ? 'korvo-bg-active korvo-text-active font-medium'
                                                      : 'text-foreground-muted'
                                                  )}
                                                  onMouseEnter={(e) => {
                                                    if (!pageActive) {
                                                      e.currentTarget.style.backgroundColor = 'var(--background-muted)';
                                                    }
                                                  }}
                                                  onMouseLeave={(e) => {
                                                    if (!pageActive) {
                                                      e.currentTarget.style.backgroundColor = 'transparent';
                                                    }
                                                  }}
                                                >
                                                  {page.title}
                                                </Link>
                                              );
                                            })}
                                          </div>
                                        </div>
                                      </div>
                                    ))
                                )}
                              </div>
                            )}
                          </div>
                        ) : (
                          <Link
                            href={item.href}
                            className={cn(
                              'w-full text-left py-3 px-4 rounded-lg transition-colors block',
                              isActive
                                ? 'korvo-bg-active korvo-text-active korvo-border-active border'
                                : 'text-foreground-default'
                            )}
                            onMouseEnter={(e) => {
                              if (!isActive) {
                                e.currentTarget.style.backgroundColor = 'var(--background-muted)';
                              }
                            }}
                            onMouseLeave={(e) => {
                              if (!isActive) {
                                e.currentTarget.style.backgroundColor = 'transparent';
                              }
                            }}
                          >
                            <div className="flex items-center gap-3">
                              <Icon className="h-4 w-4" />
                              <span className="font-medium">{item.label}</span>
                            </div>
                            <div className="text-xs text-foreground mt-1 ml-7">
                              {item.description}
                            </div>
                          </Link>
                        )}
                      </li>
                    );
                  })}
                </ul>
              </div>
            ))}
          </div>
        </nav>
  );

  return (
    <div
      className="flex flex-row"
      style={{
        backgroundColor: 'var(--background-default)',
        minHeight: 'calc(100vh - var(--topbar-height, 64px))',
      }}
    >
      {/* Left Sidebar - always visible on desktop (md+); on mobile use the top bar side menu */}
      <div
        className={cn('shrink-0 hidden md:block w-[260px] py-4 pl-4 pr-3')}
        style={{
          backgroundColor: 'var(--background-secondary)',
          borderRight: '1px solid var(--border-default)',
        }}
      >
        <div className="mb-6" data-testid="admin-settings-panel">
          <h1 className="text-xl font-semibold text-foreground-default">Admin Settings</h1>
          <p className="text-sm text-foreground-muted mt-1">Configure system settings</p>
        </div>
        {navContent}
      </div>

      {/* Right Content - scrollable on mobile; sidebar is md+ only */}
      <div
        className="flex-1 min-w-0 min-h-0 overflow-y-auto px-3 py-4 sm:px-4 sm:py-5 lg:px-5 lg:py-6"
        style={{
          backgroundColor: 'var(--background-default)',
        }}
        data-testid="admin-settings-main"
      >
        {children}
      </div>
    </div>
  );
}
